import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import mixpanel from 'mixpanel-browser';
import useShopifyInstall, {
  cleanupShopParam,
} from '../hooks/useShopifyInstall';
import ErrorPage from '../auth/ErrorPage';
import LoadingPage from '../auth/LoadingPage';

/**
 * Landing page to allow starting the install process. Requires the `?shop=xxx.myshopify.com` parameter set.
 *
 * You can also optionally set the `vst_campaign` query param. If this is present we'll update the
 * `Latest Campaign` property of the user in Mixpanel.
 *
 * Normally installation is done from the app store so this is mainly used for campaign style links.
 */
const Install: React.FC = () => {
  const [error, setError] = useState(false);
  const location = useLocation();
  const search = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const { authorizationUrl } = useShopifyInstall();

  useEffect(() => {
    if (!authorizationUrl) {
      return;
    }

    const shop = search.get('shop');
    if (!shop) {
      setError(true);
      return;
    }

    // Fire-and-forget tracking so we don't delay the OAuth redirect
    // (Shopify App Store rule 2.3.2 — authenticate immediately).
    try {
      mixpanel.identify(cleanupShopParam(shop));
      mixpanel.track('Install Landing Page');
    } catch (e) {
      console.error('Caught error tracking', e);
    }

    window.location.assign(authorizationUrl);
  }, [search, authorizationUrl]);

  if (error) {
    return <ErrorPage />;
  }

  return <LoadingPage />;
};

export default Install;
